import { useCallback } from "react";
import type { ActorRefFrom } from "xstate";
import type { transferArtworkMachine } from "@design-lab/entities/transfers-lab/model/transfer-artwork.machine";

/**
 * Hook to access the sizes form control from an artwork actor
 * This allows components to interact with the form data for each artwork
 */
export function useArtworkSizesForm(
  artworkActor: ActorRefFrom<typeof transferArtworkMachine> | null | undefined
) {
  // Use conditional selectors but ensure hooks are always called
  const snapshot = artworkActor ? artworkActor.getSnapshot() : null;
  const sizesFormControl = snapshot?.context.sizesFormControl;
  const quantity = snapshot?.context.quantity || 0;

  // Get the single artwork ID from the form
  const getArtworkId = useCallback(() => {
    if (!sizesFormControl) return null;
    const formValues = sizesFormControl.getValues();
    return formValues.artworkOrder?.[0] || null;
  }, [sizesFormControl]);

  // Calculate total quantity from form data
  const calculateTotalQuantity = useCallback(() => {
    if (!sizesFormControl) return 0;

    const formValues = sizesFormControl.getValues();
    const artworkId = getArtworkId();
    if (!artworkId) return 0;

    const artwork = formValues.artworks?.[artworkId];
    if (!artwork) return 0;

    let total = 0;

    // Sum quantities across all transfer types
    Object.values(artwork?.transferTypes || {}).forEach((transferType: any) => {
      // Sum preset quantities
      Object.values(transferType?.presets || {}).forEach((preset: any) => {
        preset?.sizes?.forEach((size: any) => {
          const qty = parseInt(size.quantity || 0, 10);
          if (qty > 0) {
            total += qty;
          }
        });
      });

      // Sum custom preset quantities
      transferType?.customPreset?.customSizes?.forEach((size: any) => {
        const qty = parseInt(size.quantity || 0, 10);
        if (qty > 0) {
          total += qty;
        }
      });
    });

    return total;
  }, [sizesFormControl, getArtworkId]);

  // Update the quantity in the state machine
  const updateQuantity = useCallback(() => {
    if (!artworkActor || !sizesFormControl) return;
    const total = calculateTotalQuantity();

    // Always update the quantity, even if it's the same
    // This ensures the UI stays in sync
    artworkActor.send({ type: "quantity.set", value: total });
  }, [artworkActor, calculateTotalQuantity, sizesFormControl]);

  // Add a transfer type
  const addTransferType = useCallback(
    (transferTypeId: string, transferTypeName: string) => {
      if (!sizesFormControl) return;

      const artworkId = getArtworkId();
      if (!artworkId) return;

      const currentValues = sizesFormControl.getValues();
      const artwork = currentValues.artworks?.[artworkId];
      if (!artwork) return;

      // Check if transfer type already exists
      if (artwork.transferTypes[transferTypeId]) return;

      // Create the complete updated artwork data
      const updatedArtwork = {
        ...artwork,
        transferTypes: {
          ...artwork.transferTypes,
          [transferTypeId]: {
            id: transferTypeId,
            name: transferTypeName,
            presets: {},
            presetOrder: [],
            customPreset: {
              id: `custom-${transferTypeId}`,
              customSizes: []
            }
          }
        }
      };

      // Set the entire artwork object at once
      sizesFormControl.setValue(`artworks.${artworkId}`, updatedArtwork);
    },
    [sizesFormControl, getArtworkId]
  );

  // Remove a transfer type
  const removeTransferType = useCallback(
    (transferTypeId: string) => {
      if (!sizesFormControl) return;

      const artworkId = getArtworkId();
      if (!artworkId) return;

      const currentValues = sizesFormControl.getValues();
      const artwork = currentValues.artworks?.[artworkId];
      if (!artwork) return;

      // Remove from transferTypes
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [transferTypeId]: _, ...remainingTypes } = artwork.transferTypes;

      // Create the complete updated artwork data
      const updatedArtwork = {
        ...artwork,
        transferTypes: remainingTypes
      };

      // Set the entire artwork object at once
      sizesFormControl.setValue(`artworks.${artworkId}`, updatedArtwork);
    },
    [sizesFormControl, getArtworkId]
  );

  return {
    sizesFormControl,
    artworkId: getArtworkId(),
    quantity,
    calculateTotalQuantity,
    updateQuantity,
    addTransferType,
    removeTransferType,
    isInitialized: !!sizesFormControl
  };
}
